import { isSubscriptionExpiredError } from "../../utils/subscriptionExpiredDialog";
import React, { useEffect, useState, useRef, useCallback } from "react";
import PageTitle from "../../components/PageTitle";
import { useAxiosPost } from "../../hooks/useAxios";
import {
  Link,
  useLocation,
  useParams,
  useNavigationType,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useGstFetchTracker } from "../../context/GstFetchTrackerContext";
import TenantLink from "../../components/TenantLink";
import { ServiceType } from "../../utils/enums";
import { Table } from "../../components/Table";
import WarningImage from "../../assets/images/Warning.png";
import {
  Button,
  DatePicker,
  Row,
  Col,
  Card,
  Popover,
  Modal,
  Breadcrumb,
  Tooltip,
  Input,
  Progress,
  Alert,
  Typography,
} from "antd";
import { FaFilter, FaList, FaPlus } from "react-icons/fa";
import {
  FilterIcon,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Download,
} from "lucide-react";
import moment from "moment";
import { Select, Tag } from "antd";
import { CalendarOutlined, DeleteFilled, SyncOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  deleteNotices,
  fetchData,
  getNoticeListData,
  postData,
} from "../../api/apiService";
import { CloseOutlined } from "@ant-design/icons";
import {
  getClientDocType,
  getClientsFields,
  getDocType,
} from "../../utils/helpers";
import DashboardButton from "../../components/DashboardButton";
import UserFilterDropdown, {
  ALL_USERS,
  toQueryParam as userFilterToQueryParam,
} from "../../components/UserFilterDropdown";
import ClientAutoComplete from "../../components/ClientAutoComplete";
import { motion, AnimatePresence } from "framer-motion";
import TableV2 from "../../components/TableV2";
import DownloadDataButton from "../../components/DownloadDataButton";
import NoticeSummaryViewer from "../../components/NoticeSummaryViewer";
import { useFeatureEnabled } from "../../hooks/usePlanFeatures";
import { FEATURE_KEYS } from "../../utils/featureKeys";
import FetchNoticesConfirmModal from "../../components/FetchNoticesConfirmModal";
import DeleteAllNoticesButton from "../../components/DeleteAllNoticesButton";
import BulkStatusButton from "../../components/BulkStatusButton";
import { LAMBDA, DOCUMENTS } from "../../api/endpointMap";

const { RangePicker } = DatePicker;
const { Option } = Select;

// URL `?type=<card>` → BE `notice_mode` preset. Mirrors
// Eproceedings.jsx:622 so GST cards filter server-side just like IT.
// Returns null for "total_notices" / unknown / failed_login → caller
// omits the param entirely (no preset filter on the BE).
const getNoticeMode = (type) => {
  const map = {
    total_notices: "total",
    last_3_days_notices: "last_3_days",
    last_15_days_notices: "last_15_days",
    open_notices: "open",
    over_due_notices: "over_due",
    due_7_days_notices: "due_within_7",
  };
  return map[type] || null;
};

// Build the column-filter query-param dict from the GST table's
// `filterValues` state + URL `?due_date=...`. Mirrors the BE shape
// declared on /api/v1/gst/notices (+ /additional-notices): single
// scalars for ILIKE/exact columns, paired `*_from`/`*_to` for date
// ranges, plus exact `due_date` for calendar-click drill-downs.
// `fv` may be null/empty (failed_login tab does not populate it) —
// in that case only the dueDateParam branch contributes.
const buildGstFilterParams = (fv, dueDateParam, formatDate) => {
  const p = {};
  if (fv) {
    if (fv.client_name?.trim?.()) p.client_name = fv.client_name.trim();
    if (fv.ref_id?.trim?.()) p.ref_id = fv.ref_id.trim();
    if (fv.type?.trim?.()) p.type = fv.type.trim();
    if (fv.notice_status?.trim?.()) p.notice_status = fv.notice_status.trim();
    if (fv.response_status) p.response_status = fv.response_status;
    if (Array.isArray(fv.issuedDate) && fv.issuedDate.length === 2) {
      const [from, to] = fv.issuedDate;
      if (from && to) {
        p.issue_from = formatDate(from);
        p.issue_to = formatDate(to);
      }
    }
    if (Array.isArray(fv.dueDate) && fv.dueDate.length === 2) {
      const [from, to] = fv.dueDate;
      if (from && to) {
        p.due_from = formatDate(from);
        p.due_to = formatDate(to);
      }
    }
  }
  // Exact due_date from calendar click (URL param) — takes precedence
  // over a range when both are set; BE applies both filters so the
  // intersection is empty in that pathological case (intentional).
  if (dueDateParam) p.due_date = formatDate(dueDateParam);
  return p;
};

export const GSTNoticeList = ({ serviceType }) => {
  // Plan-feature gate for the "View Summary" column (VIEW_NOTICE_SUMMARY via
  // /api/v1/subscriptions/features). Column dropped entirely when disabled.
  const summaryFeatureEnabled = useFeatureEnabled(
    FEATURE_KEYS.VIEW_NOTICE_SUMMARY,
  );
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const {
    startTracking: startGlobalFetchTracking,
    logId: activeGlobalLogId,
  } = useGstFetchTracker();
  const [notices, setNotices] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  // Starts TRUE: this page always fetches on mount, so the first paint
  // must show the loader - initialising false flashed "No data available"
  // before the fetch's own setLoading(true) ran.
  const [loading, setLoading] = useState(true);
  const [selectedNoticeIds, setSelectedNoticeIds] = useState([]);
  const [clientsData, setClientsData] = useState([]);

  const [showFilters, setShowFilters] = useState(true);
  const [noticeTypeValues, setNoticeTypeValue] = useState([]);
  const [sectionValues, setSectionValues] = useState([]);
  const [noticeStatusValues, setNoticeStatusValues] = useState([]);
  // User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). ALL_USERS sentinel
  // → omit assigned_user_id from the payload; BE applies role-default scope.
  const [userFilter, setUserFilter] = useState(ALL_USERS);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [limitStart, setLimitStart] = useState(0);
  const [limit, setLimit] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);

  const [failedLoginRecords, setFailedLoginCount] = useState(null);
  const [normalNoticeCount, setNormalNoticeCount] = useState(null);
  const [additionalNoticeCount, setAdditionalNoticeCount] = useState(null);

  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [processedClients, setProcessedClients] = useState([]);

  // Progress polling state for failed login retry
  const [parallelFetchLogName, setParallelFetchLogName] = useState(null);
  const [fetchProgress, setFetchProgress] = useState(null);
  const [batchPollingInterval, setBatchPollingInterval] = useState(null);
  const [syncCompletionModal, setSyncCompletionModal] = useState(false);
  const isBatchFetchActive = useRef(false);

  const { Text, Title } = Typography;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const noticeType = queryParams.get("type");
  const noticeDibaledType = queryParams.get("disabledType") ?? "active";
  const dueDateParam = queryParams.get("due_date");
  const clientName = queryParams.get("client_name");
  const activeTabParam = queryParams.get("activeTab");

  // Storage key for filter persistence
  const FILTER_STORAGE_KEY = `gst_notices_filters_${clientId || "all"}`;
  // Storage key for active tab persistence
  const TAB_STORAGE_KEY = `gst_notices_activeTab_${clientId || "all"}`;

  // Detect navigation type: POP = browser back/forward, PUSH/REPLACE = fresh navigation
  const navigationType = useNavigationType();

  // Initialize activeNoticeTab - restore from session on back navigation, otherwise from URL or default
  const [activeNoticeTab, setActiveNoticeTab] = useState(() => {
    // If activeTab is in URL params, use it (coming from detail page)
    if (activeTabParam) {
      return activeTabParam === "additional" ? "additional" : "normal";
    }
    // On browser back navigation, try to restore from sessionStorage
    if (navigationType === "POP") {
      const savedTab = sessionStorage.getItem(TAB_STORAGE_KEY);
      if (savedTab === "additional" || savedTab === "normal") {
        return savedTab;
      }
    }
    // Default to Additional Notices (opened first by product decision).
    return "additional";
  });

  // Save active tab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(TAB_STORAGE_KEY, activeNoticeTab);
  }, [activeNoticeTab, TAB_STORAGE_KEY]);

  // Load saved filters from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert date strings back to moment objects if needed
        if (parsed.issuedDate && Array.isArray(parsed.issuedDate)) {
          parsed.issuedDate = parsed.issuedDate.map((d) =>
            d ? moment(d) : null
          );
        }
        if (parsed.dueDate && Array.isArray(parsed.dueDate)) {
          parsed.dueDate = parsed.dueDate.map((d) => (d ? moment(d) : null));
        }
        return parsed;
      }
    } catch (e) {
      console.error("Error loading saved filters:", e);
    }
    return null;
  };

  const getDefaultFilters = () => {
    return noticeType == "failed_login"
      ? { client_name: null }
      : {
          client_name: null,
          ref_id: null,
          notice_type: null,
          type: null,
          response_status: null,
          notice_status: null,
          issuedDate: null,
          dueDate: null,
        };
  };

  // Clear filters from sessionStorage (used when navigating via breadcrumb)
  const clearSavedFilters = () => {
    try {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    } catch (e) {
      console.error("Error clearing saved filters:", e);
    }
  };

  // Only restore filters on browser back (POP), not on fresh navigation (PUSH/REPLACE)
  const [filterValues, setFilterValues] = useState(() => {
    if (navigationType === "POP") {
      // Browser back/forward - restore saved filters
      const savedFilters = getSavedFilters();
      return savedFilters || getDefaultFilters();
    } else {
      // Fresh navigation (clicking card/link) - clear saved filters and start fresh
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
      return getDefaultFilters();
    }
  });

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      const filtersToSave = { ...filterValues };
      if (filtersToSave.issuedDate && Array.isArray(filtersToSave.issuedDate)) {
        filtersToSave.issuedDate = filtersToSave.issuedDate.map((d) =>
          d ? d.format() : null
        );
      }
      if (filtersToSave.dueDate && Array.isArray(filtersToSave.dueDate)) {
        filtersToSave.dueDate = filtersToSave.dueDate.map((d) =>
          d ? d.format() : null
        );
      }
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (e) {
      console.error("Error saving filters:", e);
    }
  }, [filterValues, FILTER_STORAGE_KEY]);

  // Get filtered client name for breadcrumb display
  const getFilteredClientName = () => {
    return filterValues?.client_name?.trim() || null;
  };

  const getColumnWidths = () => {
    if (noticeType == "failed_login") {
      return {
        client_name: "200px",
        portal: "150px",
        created: "250px",
      };
    }
    return {
      client_name: "25%",
      notice_type: "12%",
      ref_id: "12%",
      type: "12%",
      issue_date: "10%",
      due_date: "10%",
      response_status: "10%",
      notice_status: "9%",
    };
  };

  const columnWidths = getColumnWidths();

  const formatDate = (date) => {
    if (!date) return "";

    if (typeof date.format === "function") {
      return date.format("YYYY-MM-DD");
    }

    if (
      Object.prototype.toString.call(date) === "[object Date]" &&
      !isNaN(date.getTime())
    ) {
      return date.toISOString().slice(0, 10);
    }

    const m = moment(date);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  };

  // Legacy Frappe-era data fetch. The doctype-keyed REST endpoint
  // (`/GST Client`) never existed on the FastAPI backend, so this call
  // has been returning 404s since the migration. The `clientsData`
  // state it populated is unread elsewhere — dead. Left as a no-op so
  // the call site at the init effect below doesn't need to be touched
  // and the console isn't spammed with 404s on every mount. Mirrors the
  // already-applied fix in `pages/income-tax/Eproceedings.jsx:349-358`.
  const getClientsData = async () => {};

  const [fetchNoticeCount] = useAxiosPost(DOCUMENTS.GET_COUNT);

  const columns =
    noticeType == "failed_login"
      ? [
          {
            label: "Portal",
            key: "portal",
            width: columnWidths.portal,
            filterable: false,
            filterName: "portal",
          },
          {
            label: "Client",
            key: "client_name",
            filterable: false,
            filterName: "client_name",
            width: columnWidths.client_name,
          },
          {
            label: "Initiated",
            key: "creation",
            filterable: false,
            filterName: "creation",
            width: columnWidths.created,
            render: (row) => {
              const formattedDate = row["creation"]
                ? moment(row["creation"]).format("MMMM Do YYYY, h:mm:ss a")
                : "-";

              return row["creation"] ? (
                <div className="flex items-center h-14 justify-center">
                  <Tag
                    icon={<CalendarOutlined />}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "200px",
                      padding: "5px 10px",
                    }}
                  >
                    {formattedDate}
                  </Tag>
                </div>
              ) : (
                <p>-</p>
              );
            },
          },
        ]
      : [
          {
            label: "Client Name",
            key: "client_name",
            filterable: true,
            filterType: "autocomplete",
            filterName: "client_name",
            width: columnWidths.client_name,
          },
          {
            label: "Reference ID",
            key: "ref_id",
            filterType: "text",
            width: columnWidths.ref_id,
            filterable: true,
            filterName: "ref_id",
          },
          {
            label: "Type",
            key: "type",
            width: columnWidths.type,
            filterable: true,
            filterName: "type",
            filterType: "text",
          },
          {
            label: "Issued Date",
            key: "issue_date",
            filterable: true,
            filterType: "date",
            filterName: "issuedDate",
            width: columnWidths.issue_date,
            render: (row) => {
              const date = row?.issue_date;
              const formattedDate = date
                ? date.split("-").reverse().join("-")
                : "N/A";
              return (
                <div className="flex justify-center mr-2">
                  <Tag color="#108ee9">{formattedDate}</Tag>
                </div>
              );
            },
          },
          {
            label: "Due Date",
            key: "due_date",
            width: columnWidths.due_date,
            filterable: true,
            filterType: "date",
            filterName: "dueDate",
            render: (row) => {
              const date = row?.due_date;
              const formattedDate = date
                ? date.split("-").reverse().join("-")
                : "N/A";
              return (
                <div className="flex justify-center mr-2">
                  <Tag color="#108ee9">{formattedDate}</Tag>
                </div>
              );
            },
          },
          {
            label: "View Summary",
            key: "view_summary",
            filterable: false,
            filterType: "none",
            preventRowClick: true,

            render: (row) => {
              // Hide the button only when the row has no usable PDF source.
              // For regular GST Notices the source is `notice_letter`. For
              // Additional Notices the column is NULL by design (Lambda-
              // ingested rows keep PDFs on the linked `case_details`
              // children) — the BE summary pipeline already pulls those
              // via `notice_summary.py` (`use_case_details_files=True`).
              // Gating on notice_letter for additional notices wrongly
              // hid the button on 100% of them.
              const isAdditional = row?.notice_type == "Additional Notices";
              if (!row?.notice_letter && !isAdditional) {
                // Regular GST Notice with no notice_letter PDF → surface
                // a muted "No Notice File" label so the cell isn't
                // ambiguously blank (mirrors the IT Eproceedings list).
                return (
                  <span className="text-slate-400 text-xs italic ml-14">
                    No Notice File
                  </span>
                );
              }
              return (
                <div className="ml-14">
                  <NoticeSummaryViewer
                    variant="table"
                    doctype={getDocType(
                      isAdditional ? "GSTADDNOTICE" : "GSTNOTICE"
                    )}
                    buttonClassName="rounded-full w-4 text-lg"
                    docname={row?.name}
                  />
                </div>
              );
            },
          },
          {
            label: "Response Status",
            key: "response_status",
            width: columnWidths.response_status,
            filterType: "select",
            filterable: true,
            sortable: true,
            filterName: "response_status",
            filterOptions: [
              { label: "Pending", value: "Pending" },
              { label: "Response Generated", value: "Response Generated" },
              { label: "Response Finalized", value: "Response Finalized" },
              { label: "Submitted", value: "Submitted" },
            ],
            render: (row) => {
              const statusColors = {
                Pending: "#d9d9d9",
                "Response Generated": "#faad14",
                "Response Finalized": "#1890ff",
                Submitted: "#52c41a",
              };
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "cenetr",
                    justifyContent: "center",
                  }}
                >
                  <Tag
                    color={statusColors[row?.response_status] || "#d9d9d9"}
                    style={{ padding: "2px 10px" }}
                  >
                    {row?.response_status || "Pending"}
                  </Tag>
                </div>
              );
            },
          },
          {
            label: "Portal Status",
            key: "notice_status",
            width: columnWidths.notice_status,
            filterType: "text",
            filterable: true,
            sortable: true,
            filterName: "notice_status",
          },
        ];

  const renderFilterRow = () => {
    if (noticeType == "failed_login") {
      return null;
    }

    const activeFiltersCount = Object.values(filterValues).filter(
      (v) => v && v !== ""
    ).length;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-lg mb-4 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/3 to-indigo-600/3"></div>

        <div className="relative bg-white/90 backdrop-blur-sm border-b border-blue-200/50 px-6 py-3">
          <div className="flex items-center justify-between">
            <motion.div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setIsExpanded(!isExpanded)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="bg-blue-800 p-2 rounded-lg shadow-md">
                <FilterIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                  Advanced Filters
                  {activeFiltersCount > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-800 text-white text-xs px-2 py-1 rounded-full font-medium"
                    >
                      {activeFiltersCount}
                    </motion.div>
                  )}
                </h3>
                <p className="text-xs text-blue-600">
                  {isExpanded ? "Click to collapse" : "Click to expand filters"}
                </p>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={clearFilters}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-all duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Clear All
                </motion.button>
              )}

              <motion.button
                onClick={() => setIsExpanded(!isExpanded)}
                className="bg-blue-800 text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-blue-900 transition-all duration-200 shadow-md flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{isExpanded ? "Collapse" : "Expand"}</span>
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              </motion.button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="relative p-6">
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className="grid lg:grid-cols-6 xl:grid-cols-6 gap-4"
                >
                  {/* Client Name Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Client Name
                    </span>
                    <div className="relative">
                      <ClientAutoComplete
                        serviceType={serviceType}
                        handleSelect={(value) =>
                          handleFilterValueChange("client_name", value)
                        }
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                      />
                    </div>
                  </motion.div>

                  {/* Reference ID Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Reference ID
                    </span>
                    <div className="relative">
                      <Input
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        value={filterValues.ref_id}
                        onChange={(e) =>
                          handleFilterValueChange("ref_id", e.target.value)
                        }
                        placeholder="Filter Ref ID..."
                        allowClear
                        prefix={<div className="text-blue-400">🔍</div>}
                      />
                    </div>
                  </motion.div>

                  {/* Type/Description Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Type/Description
                    </span>
                    <div className="relative">
                      <Input
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        value={filterValues.type}
                        onChange={(e) =>
                          handleFilterValueChange("type", e.target.value)
                        }
                        placeholder="Filter description..."
                        allowClear
                        prefix={<div className="text-blue-400">📝</div>}
                      />
                    </div>
                  </motion.div>

                  {/* Issue Date Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Issue Date
                    </span>
                    <div className="relative">
                      <RangePicker
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        format="DD/MM/YYYY"
                        value={filterValues.issuedDate}
                        onChange={(dates) =>
                          handleFilterValueChange("issuedDate", dates)
                        }
                        placeholder={["From Date", "To Date"]}
                        size="small"
                      />
                    </div>
                  </motion.div>

                  {/* Due Date Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Due Date
                    </span>
                    <div className="relative">
                      <RangePicker
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        format="DD/MM/YYYY"
                        value={filterValues.dueDate}
                        onChange={(dates) =>
                          handleFilterValueChange("dueDate", dates)
                        }
                        placeholder={["From Date", "To Date"]}
                        size="small"
                      />
                    </div>
                  </motion.div>

                  {/* Notice Status Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Notice Status
                    </span>
                    <div className="relative">
                      <Select
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        value={filterValues.notice_status}
                        onChange={(value) =>
                          handleFilterValueChange("notice_status", value)
                        }
                        placeholder="Filter status..."
                        allowClear
                        style={{ height: "40px" }}
                        size="small"
                        dropdownClassName="shadow-xl border-blue-200"
                      >
                        {noticeStatusValues.map((status) => (
                          <Option key={status} value={status}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              {status}
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="h-1 bg-gradient-to-r from-blue-600 to-indigo-600"></div>
      </motion.div>
    );
  };

  const buildApiFilters = (doctype) => {
    const filters = [];

    filters.push([
      doctype,
      "disabled",
      "=",
      noticeDibaledType === "active" ? 0 : 1,
      false,
    ]);

    if (clientId) {
      filters.push([doctype, "client", "=", clientId, false]);
    }

    if (dueDateParam) {
      filters.push([doctype, "due_date", "=", dueDateParam, false]);
    }

    if (noticeType == "last_3_days_notices") {
      const today = new Date();
      const previousDate = new Date();
      previousDate.setDate(today.getDate() - 2);
      filters.push([
        doctype,
        "issue_date",
        "between",
        [formatDate(previousDate), formatDate(today)],
        false,
      ]);
    }

    if (noticeType == "over_due_notices") {
      const today = new Date();
      filters.push([doctype, "due_date", "<", formatDate(today), false]);
      filters.push([doctype, "due_date", "is", "set", false]);
    }

    if (noticeType == "last_15_days_notices") {
      const today = new Date();
      const pastDate = new Date();
      pastDate.setDate(today.getDate() - 15);
      filters.push([doctype, "issue_date", ">=", formatDate(pastDate), false]);
    }

    if (noticeType == "open_notices") {
      filters.push([doctype, "notice_status", "=", "Open", false]);
    }

    if (["over_due_notices", "due_7_days_notices", "open_notices"].includes(noticeType)) {
      filters.push([doctype, "response_status", "!=", "Submitted", false]);
    }

    if (filterValues.client_name && filterValues.client_name.trim()) {
      filters.push([
        doctype,
        "client_name",
        "like",
        `%${filterValues.client_name}%`,
        false,
      ]);
    }

    if (filterValues.ref_id && filterValues.ref_id.trim()) {
      filters.push([
        doctype,
        "ref_id",
        "like",
        `%${filterValues.ref_id}%`,
        false,
      ]);
    }

    if (filterValues.type) {
      filters.push([doctype, "type", "like", `%${filterValues.type}%`, false]);
    }

    if (filterValues.notice_type) {
      filters.push([doctype, "type", "like", `%${filterValues.type}%`, false]);
    }

    if (filterValues.notice_status && filterValues.notice_status.trim()) {
      filters.push([
        doctype,
        "notice_status",
        "like",
        `%${filterValues.notice_status}%`,
        false,
      ]);
    }

    if (filterValues.response_status) {
      filters.push([
        doctype,
        "response_status",
        "=",
        filterValues.response_status,
        false,
      ]);
    }

    if (filterValues.issuedDate && filterValues.issuedDate.length === 2) {
      const [startDate, endDate] = filterValues.issuedDate;
      filters.push([
        doctype,
        "issue_date",
        "between",
        [formatDate(startDate), formatDate(endDate)],
        false,
      ]);
    }

    if (filterValues.dueDate && filterValues.dueDate.length === 2) {
      const [startDate, endDate] = filterValues.dueDate;
      filters.push([
        doctype,
        "due_date",
        "between",
        [formatDate(startDate), formatDate(endDate)],
        false,
      ]);
    }

    return filters;
  };

  const buildFailedLoginFilters = () => {
    const filters = [
      ["Login Failed", "portal", "=", "GST", false],
      ["disabled", "=", 0],
    ];

    if (filterValues.client_name && filterValues.client_name.trim()) {
      filters.push([
        "Login Failed",
        "client_name",
        "like",
        `%${filterValues.client_name}%`,
        false,
      ]);
    }

    return filters;
  };

  const getNoticesCount = async () => {
    if (noticeType !== "failed_login") {
      // Phase-2 GST list migration: the legacy `/api/v1/documents/count`
      // Frappe-style POST does not exist on FastAPI, so we read each
      // tab's `total_records` from a page_length=1 GET against the real
      // REST endpoint instead. `notice_mode` (when present) makes the
      // BE filter on the same preset the card chose so the badge count
      // matches the table row count exactly.
      const userParam = userFilterToQueryParam(userFilter);
      const mode = getNoticeMode(noticeType);
      const countParams = {
        start: 0,
        page_length: 1,
        ...(clientId && { client: clientId }),
        ...(userParam !== undefined && { assigned_user_id: userParam }),
        ...(mode && { notice_mode: mode }),
        ...buildGstFilterParams(filterValues, dueDateParam, formatDate),
      };
      try {
        const normalResp = await fetchData("/api/v1/gst/notices", countParams);
        const normalTotal = normalResp?.data?.total_records || 0;
        setNormalNoticeCount(normalTotal);
        if (activeNoticeTab === "normal") setTotalRecords(normalTotal);
      } catch (e) {
        setNormalNoticeCount(0);
      }
      try {
        const addlResp = await fetchData(
          "/api/v1/gst/additional-notices",
          countParams,
        );
        const addlTotal = addlResp?.data?.total_records || 0;
        setAdditionalNoticeCount(addlTotal);
        if (activeNoticeTab === "additional") setTotalRecords(addlTotal);
      } catch (e) {
        setAdditionalNoticeCount(0);
      }
    } else {
      // R6 — failed_login count via the new REST endpoint
      // GET /api/v1/gst/failed-logins (mirrors IT). Legacy Frappe
      // POST /documents/count with `doctype: "Login Failed"` 404s
      // on FastAPI. page_length=1 + read total_records.
      const params = { start: 0, page_length: 1 };
      if (filterValues.client_name && filterValues.client_name.trim()) {
        params.client_name = filterValues.client_name.trim();
      }
      const userParam = userFilterToQueryParam(userFilter);
      if (userParam !== undefined) params.assigned_user_id = userParam;
      try {
        const data = await fetchData("/api/v1/gst/failed-logins", params);
        const total = data?.result?.total_records || 0;
        setFailedLoginCount(total);
        setTotalRecords(total);
      } catch (e) {
        setFailedLoginCount(0);
        setTotalRecords(0);
      }
    }
  };

  const getNoticePageName = (type) => {
    switch (type) {
      case "open_notices":
        return "Open Notices";
      case "last_3_days_notices":
        return "Notices Received in last 3 days";
      case "due_7_days_notices":
        return "Due within 7 days";
      case "failed_login":
        return "Failed Logins";
      case "last_15_days_notices":
        return "Last 15 Days Notices";
      case "over_due_notices":
        return "Over Due Notices";
      default:
        return "Total Notices";
    }
  };

  // Fetch ALL records (all pages) matching current filters — used by
  // TableV2 PDF/CSV download buttons. Loops in batches of 500 until the
  // last batch returns < 500 records. Mirrors getTotalGSTNoticeList
  // params/endpoint exactly for the active tab.
  const fetchAllForExport = async () => {
    const PAGE_SIZE = 500;
    const all = [];
    let start = 0;

    if (noticeType === "failed_login") {
      // R6 — failed_login export via the new REST endpoint.
      const baseParams = {};
      if (filterValues.client_name && filterValues.client_name.trim()) {
        baseParams.client_name = filterValues.client_name.trim();
      }
      const userParam = userFilterToQueryParam(userFilter);
      if (userParam !== undefined) baseParams.assigned_user_id = userParam;
      while (true) {
        const resp = await fetchData("/api/v1/gst/failed-logins", {
          ...baseParams,
          start,
          page_length: PAGE_SIZE,
        });
        // Pre-format creation as the page-list render does, so CSV/PDF
        // show "May 19th 2026, 2:25:26 pm" instead of raw ISO. TableV2's
        // export reads row[col.key] and skips render() for string values.
        const batch = (resp?.result?.data || []).map((r) => ({
          ...r,
          creation: r.creation
            ? moment(r.creation).format("MMMM Do YYYY, h:mm:ss a")
            : "",
        }));
        all.push(...batch);
        if (batch.length < PAGE_SIZE) break;
        start += PAGE_SIZE;
      }
      return all;
    }

    // Mirror getTotalGSTNoticeList's REST path so export uses the same
    // FastAPI endpoints as the on-screen list. Legacy `/{doctype}` path
    // (getNoticeListData) returns 404 on FastAPI. notice_mode handles
    // due_7_days_notices server-side — no client-side or_filters needed.
    const noticeTypeLabel = activeNoticeTab === "normal"
      ? "Notices & Orders"
      : "Additional Notices";
    const endpoint = activeNoticeTab === "normal"
      ? "/api/v1/gst/notices"
      : "/api/v1/gst/additional-notices";
    const userParam = userFilterToQueryParam(userFilter);
    const mode = getNoticeMode(noticeType);

    while (true) {
      const restParams = {
        start,
        page_length: PAGE_SIZE,
        ...(clientId && { client: clientId }),
        ...(userParam !== undefined && { assigned_user_id: userParam }),
        ...(mode && { notice_mode: mode }),
        ...buildGstFilterParams(filterValues, dueDateParam, formatDate),
      };

      const resp = await fetchData(endpoint, restParams);
      const batch = (resp?.data?.records || []).map((item) => ({
        ...item,
        notice_type: noticeTypeLabel,
      }));
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
      start += PAGE_SIZE;
    }
    return all;
  };

  const getTotalGSTNoticeList = async (start, itemsPerPage) => {
    try {
      setLoading(true);

      if (noticeType == "failed_login") {
        // R6 — failed_login list via the new REST endpoint
        // GET /api/v1/gst/failed-logins (mirrors IT). Returns
        // `data.result.data` (array) + `data.result.total_records`.
        const params = { start, page_length: itemsPerPage };
        if (filterValues.client_name && filterValues.client_name.trim()) {
          params.client_name = filterValues.client_name.trim();
        }
        const userParam = userFilterToQueryParam(userFilter);
        if (userParam !== undefined) params.assigned_user_id = userParam;
        const data = await fetchData("/api/v1/gst/failed-logins", params);
        const failedLoginNotices = data?.result?.data || [];
        const total = data?.result?.total_records || 0;
        setNotices(failedLoginNotices);
        setTotalRecords(total);
        // Also set failedLoginRecords so the table's `totalRecords` prop
        // (which reads failedLoginRecords for failed_login type) populates.
        setFailedLoginCount(total);
      } else {
        const fields = [
          "name",
          "ref_id",
          "client",
          "client_name",
          "type",
          "issue_date",
          "due_date",
          "response_status",
          "notice_status",
        ];

        let notices = [];

        // Build or_filters for due_7_days_notices (date range OR not set)
        let orFiltersParam = undefined;
        if (noticeType == "due_7_days_notices") {
          const today = new Date();
          const futureDates = new Date();
          futureDates.setDate(today.getDate() + 7);
          orFiltersParam = JSON.stringify([
            ["due_date", "between", [formatDate(today), formatDate(futureDates)]],
            ["due_date", "is", "not set"],
          ]);
        }

        // Phase-2 GST list migration: hit the FastAPI REST endpoints
        // (gst.py:127 + :320) instead of the dead Frappe-style doctype
        // URLs. Both endpoints accept the same simple param shape and
        // return `{ data: { total_records, records } }`. `notice_mode`
        // (when present) tells the BE which dashboard-card preset to
        // apply so the rendered table matches the card the user clicked.
        const userParam = userFilterToQueryParam(userFilter);
        const mode = getNoticeMode(noticeType);
        const restParams = {
          start,
          page_length: itemsPerPage,
          ...(clientId && { client: clientId }),
          ...(userParam !== undefined && { assigned_user_id: userParam }),
          ...(mode && { notice_mode: mode }),
          ...buildGstFilterParams(filterValues, dueDateParam, formatDate),
        };

        if (activeNoticeTab === "normal") {
          const normalData = await fetchData("/api/v1/gst/notices", restParams);
          notices = (normalData?.data?.records || []).map((item) => ({
            ...item,
            notice_type: "Notices & Orders",
          }));
          setTotalRecords(normalData?.data?.total_records || 0);
        } else {
          // activeNoticeTab === "additional"
          const additionalData = await fetchData(
            "/api/v1/gst/additional-notices",
            restParams,
          );
          notices = (additionalData?.data?.records || []).map((item) => ({
            ...item,
            notice_type: "Additional Notices",
          }));
          setTotalRecords(additionalData?.data?.total_records || 0);
        }

        setNotices(notices);

        if (notices && notices.length > 0) {
          const uniqueType = [
            ...new Set(notices.map((item) => item.type)),
          ].filter(Boolean);
          const uniqueSections = [
            ...new Set(notices.map((item) => item.notice_type)),
          ].filter(Boolean);
          const uniqueStatuses = [
            ...new Set(notices.map((item) => item.notice_status)),
          ].filter(Boolean);

          setSectionValues(uniqueSections);
          setNoticeTypeValue(uniqueType);
          setNoticeStatusValues(uniqueStatuses);
        }
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleFilterValueChange = (filterName, value) => {
    console.log("Filter Name:", filterName, "Value:", value);
    setFilterValues((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const clearFilters = () => {
    setFilterValues(
      noticeType == "failed_login"
        ? {
            client_name: null,
          }
        : {
            client_name: null,
            ref_id: null,
            notice_type: null,
            type: null,
            notice_status: null,
            issuedDate: null,
            dueDate: null,
          }
    );

    setLimitStart(0);
  };

  const applyFilters = () => {
    setLimitStart(0);
    getNoticesCount();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      applyFilters();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    filterValues.client_name,
    filterValues.ref_id,
    filterValues.type,
    filterValues.response_status,
    filterValues.notice_status,
    filterValues.issuedDate,
    filterValues.dueDate,
  ]);

  useEffect(() => {
    getNoticesCount();
  }, [clientId, noticeType]);

  useEffect(() => {
    if (
      noticeType !== "failed_login" &&
      normalNoticeCount !== null &&
      additionalNoticeCount !== null
    ) {
      setTotalRecords(normalNoticeCount + additionalNoticeCount);
      getTotalGSTNoticeList(limitStart, limit);
    } else if (noticeType === "failed_login" && failedLoginRecords !== null) {
      getTotalGSTNoticeList(limitStart, limit);
    }
    // userFilter: re-fetch when the user-filter dropdown changes
    // (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2).
  }, [
    normalNoticeCount,
    additionalNoticeCount,
    failedLoginRecords,
    limitStart,
    limit,
    noticeType,
    activeNoticeTab,
    userFilter,
  ]);

  // Check for active fetch on page load (handles page refresh mid-fetch)
  // Scoped to GST portal so IT/TDS fetches don't affect this page
  const checkActiveFetch = async () => {
    try {
      const response = await postData(
        LAMBDA.GET_FETCH_STATUS_GST,
        { portal_type: "GST" }
      );
      // FastAPI returns service payload under response.data.result (same fix
      // as Eproceedings.jsx:947-949). Without `data.result` first in the
      // fallback chain, `result` ends up as the response.message STRING
      // ("Status fetched"), which breaks the if-check below.
      const result =
        response?.data?.result || response?.result || response?.message || {};
      if (result?.status === "success" && !result?.is_complete) {
        // A fetch is still in progress — disable buttons and resume polling
        setIsProcessingAll(true);
        setParallelFetchLogName(result.log_name);
        // Only ADOPT the discovered "latest active" log if we aren't already
        // tracking one. Otherwise a scheduled/system run that started after
        // the user's manual fetch would clobber the user's log here — and the
        // user's timing/method panel would vanish from the result popup.
        if (!activeGlobalLogId) {
          startGlobalFetchTracking(result.log_name);
        }
        startBatchPolling(result.log_name);
      }
    } catch (err) {
      console.error("Error checking active fetch:", err);
    }
  };

  useEffect(() => {
    getClientsData();
    checkActiveFetch();
    // R7 — mirror IT pattern (Eproceedings.jsx:961-972): for failed_login,
    // fetch the list directly on mount instead of relying on the indirect
    // count → list useEffect chain (which can be flaky if `failedLoginRecords`
    // stays null because the count call fails or races). The list fetch
    // also sets `failedLoginRecords` (R7 — getTotalGSTNoticeList branch).
    if (noticeType === "failed_login") {
      getTotalGSTNoticeList(limitStart, limit);
    }
  }, []);

  // Core delete for a given id array. Extracted so BOTH the "Bulk Delete"
  // (selected ids) button AND the shared "Delete All Notices" button (every id
  // in the current list scope) route through the exact same endpoint/doctype
  // logic. Throws on failure so callers can react; does NOT toast or refresh.
  const deleteNoticesByIds = async (ids) => {
    if (!ids || ids.length === 0) return;
    if (noticeType === "failed_login") {
      await deleteNotices(DOCUMENTS.DELETE, {
        doctype: "Login Failed",
        items: JSON.stringify(ids),
      });
      return;
    }
    // Split by ACTIVE TAB rather than parsing the ID string. After the
    // FastAPI migration, both notice + additional-notice IDs are pure
    // UUIDs, so the legacy `id.includes("ADD")` heuristic produced
    // EMPTY additionalNotices and silently failed the delete.
    const isAdditionalTab = activeNoticeTab === "additional";
    if (isAdditionalTab) {
      await deleteNotices(DOCUMENTS.DELETE_NOTICES_GST, {
        doctype: "GST Additional Notice And Order",
        docnames: JSON.stringify(ids),
      });
    } else {
      await deleteNotices(DOCUMENTS.DELETE_NOTICES_GST, {
        doctype: "GST Notice And Order",
        docnames: JSON.stringify(ids),
      });
    }
  };

  // Post-delete refresh, shared by both delete paths.
  const refreshAfterDelete = () => {
    getNoticesCount();
    getTotalGSTNoticeList(limitStart, limit);
    setSelectedNoticeIds([]);
  };

  // All ids in the CURRENT list scope (client + filters + active tab), sourced
  // from the same paginator export uses. Feeds "Delete All Notices".
  const fetchAllNoticeIdsForDelete = async () => {
    const rows = await fetchAllForExport();
    return (rows || []).map((r) => r.id || r.name).filter(Boolean);
  };

  const handleDeleteNotices = async () => {
    try {
      await deleteNoticesByIds(selectedNoticeIds);
      toast.info("Deleted Successfully");
    } catch (err) {
      toast.error(err);
    } finally {
      refreshAfterDelete();
    }
  };

  const handleOk = () => {
    setIsDeleteModalOpen(false);
    handleDeleteNotices();
  };

  const handleCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const handlePageChange = (newLimitStart, newLimit) => {
    setLimitStart(newLimitStart);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setLimitStart(0);
  };

  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);

  const handleFetchAllClientsNotices = async (forceSyncClientIds = []) => {
    setIsProcessingAll(true);

    try {
      // GST screen → the GST batch endpoint. (Was TRIGGER_PARALLEL_FETCH, the
      // Income-Tax route, which ignores portal_type and queries IncomeTaxClient
      // — so a GST "Fetch all" was triggering an Income-Tax fetch.)
      let apiUrl = LAMBDA.TRIGGER_PARALLEL_FETCH_GST;
      const payload = { portal_type: "gst" };
      if (Array.isArray(forceSyncClientIds) && forceSyncClientIds.length > 0) {
        payload.force_sync_client_ids = forceSyncClientIds;
        payload.client_names = forceSyncClientIds;
      }
      const response = await postData(apiUrl, payload);

      // FastAPI nests the batch result under data.data.result; keep the legacy
      // fallbacks. (Was missing data.data.result → log_name came back undefined
      // and progress polling never started.)
      const result =
        response?.data?.result || response?.result || response?.message || {};
      setProcessedClients(result?.client_names || []);

      // Capture log_name for progress polling
      if (result?.log_name) {
        setParallelFetchLogName(result.log_name);
        startGlobalFetchTracking(result.log_name);
      }

      setTimeout(() => {
        setShowSuccessModal(true);
      }, 2000);
    } catch (error) {
      // An expired plan is a handled state: the interceptor has already

      // shown the upgrade dialog, so neither the red console line nor the

      // generic toast belongs here.

      if (!isSubscriptionExpiredError(error)) {

        console.error("Error processing all clients:", error);

        toast.error("Failed to queue all clients for processing");

      }
      setIsProcessingAll(false);
    }
  };

  const handleFetchAllFailedLoginClientsNotices = async () => {
    setIsProcessingAll(true);

    try {
      // R6 — GST has its own retry endpoint. IT's /income-tax/fetch-failed-login
      // explicitly 400s on portal != "Income Tax" (income_tax.py:2755).
      const apiUrl = LAMBDA.FETCH_FAILED_LOGIN_GST;
      const payload = {
        portal: "GST",
      };

      const response = await postData(apiUrl, payload);
      // FastAPI nests payload under data.data.result; fall through to
      // legacy paths. Mirror Eproceedings.jsx:1467-1468.
      const resultData =
        response?.data?.result || response?.result || response?.message || {};

      setProcessedClients(resultData?.client_names || []);

      // Capture log_name for progress polling
      const logName = resultData?.log_name || resultData?.result?.log_name;
      if (logName) {
        setParallelFetchLogName(logName);
        startGlobalFetchTracking(logName);
      }

      setTimeout(() => {
        setShowSuccessModal(true);
      }, 2000);
    } catch (error) {
      // An expired plan is a handled state: the interceptor already
      // showed the upgrade dialog — no console noise, no toast.
      if (isSubscriptionExpiredError(error)) {
        setIsProcessingAll(false);
        return;
      }
      console.error("Error processing all clients:", error);
      // Surface the BE's actual error message instead of the generic
      // toast — BE returns clearer reasons like "No failed-login clients
      // to retry" (404) or portal_disabled (403). postData throws the
      // parsed BE message as a string in most cases.
      const msg =
        typeof error === "string"
          ? error
          : error?.detail?.message ||
            error?.message ||
            error?.detail ||
            "Failed to queue all clients for processing";
      toast.error(msg);
      setIsProcessingAll(false);
    }
  };

  // Start polling the Parallel Fetch Log for progress
  const startBatchPolling = useCallback((logName) => {
    if (batchPollingInterval) {
      clearInterval(batchPollingInterval);
    }

    isBatchFetchActive.current = true;

    setFetchProgress({
      total: 0,
      successful: 0,
      failed: 0,
      status: "Processing",
      isComplete: false,
    });

    const intervalId = setInterval(async () => {
      try {
        const response = await postData(
          LAMBDA.GET_FETCH_STATUS_GST,
          { log_name: logName }
        );
        // FastAPI nests service payload under response.data.result. Without
        // `data.result` first in the chain, `result` falls through to
        // `response.message` (the string "Fetch status retrieved") and the
        // status check below silently never fires → polling never stops →
        // "Fetching Notices..." progress bar gets stuck on screen even
        // after BE reports is_complete:true. Mirrors Eproceedings.jsx:1532.
        const result =
          response?.data?.result || response?.result || response?.message || {};

        if (result?.status === "success") {
          const progress = {
            total: result.total_clients || 0,
            successful: result.successful_count || 0,
            failed: result.failed_count || 0,
            status: result.fetch_status || "Processing",
            isComplete: result.is_complete || false,
            portalType: result.portal_type || "",
            portalFailureCount: result.portal_failure_count || 0,
            credentialFailureCount: result.credential_failure_count || 0,
            // Super-admin only (backend includes these only for super_admin)
            fetchMethod: result.fetch_method || null,
            hasTiming: result.has_timing || false,
            // Live per-notice progress (all users): {phase, notices_total,
            // notices_done, files_done}. Null until the engine writes it.
            notices: result.progress || null,
            logId: result.log_name || logName,
          };
          setFetchProgress(progress);

          if (progress.isComplete) {
            clearInterval(intervalId);
            setBatchPollingInterval(null);
            isBatchFetchActive.current = false;
            // The completion popup is now shown app-wide by the global
            // GstFetchResultPopup (so it appears on any page). Do NOT open
            // the old local modal here — that caused a DOUBLE popup. Just
            // refresh the on-page notice list/count.
            setIsProcessingAll(false);
            getTotalGSTNoticeList(limitStart, limit);
            getNoticesCount();
          }
        }
      } catch (err) {
        console.error("Error polling batch status:", err);
      }
      // 2.5s so the per-notice progress bar moves smoothly — the engine
      // writes a progress update every time a notice row completes.
    }, 2500);

    setBatchPollingInterval(intervalId);
  }, [batchPollingInterval]);

  // Cleanup batch polling on unmount
  useEffect(() => {
    return () => {
      if (batchPollingInterval) {
        clearInterval(batchPollingInterval);
      }
    };
  }, [batchPollingInterval]);

  return (
    <>
      <div className="flex w-full justify-between items-center">
        {clientId ? (
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax",
                    }}
                  >
                    {serviceType == "gstin"
                      ? "GST "
                      : serviceType == "tds"
                      ? "TDS"
                      : "Income Tax"}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin/clients"
                          : serviceType == "tds"
                          ? "/tds/clients"
                          : "/income-tax/clients",
                    }}
                  >
                    Clients{" "}
                  </TenantLink>
                ),
              },
              {
                // Link to THIS client's detail page, not history-back. It used
                // to be `<Link to={-1}>` (go back one step), which — when the
                // user arrived here from an additional-notice detail — reopened
                // that notice (often erroring "not found") instead of the
                // client. Falls back to a plain label when there is no clientId
                // (the all-clients notice view).
                title: clientId ? (
                  <TenantLink
                    to={{
                      pathname:
                        (serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax") + `/client-view/${clientId}`,
                    }}
                  >
                    {clientName || "Client Details"}
                  </TenantLink>
                ) : (
                  <span>{clientName || "Client Details"}</span>
                ),
              },
              { title: `Notices` },
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        ) : (
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                    onClick={clearSavedFilters}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax",
                    }}
                    onClick={clearSavedFilters}
                  >
                    {serviceType == "gstin"
                      ? "GST "
                      : serviceType == "tds"
                      ? "TDS"
                      : "Income Tax"}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <span
                    className="cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      clearSavedFilters();
                      setFilterValues(getDefaultFilters());
                    }}
                  >
                    {getNoticePageName(noticeType)}
                  </span>
                ),
              },
              // Show client name in breadcrumb when filtered
              ...(getFilteredClientName()
                ? [
                    {
                      title: (
                        <span className="text-blue-600 font-medium">
                          {getFilteredClientName()}
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        )}
        <DashboardButton serviceType={serviceType} />
      </div>

      <PageTitle
        title={clientId ? "GST Notice List" : getNoticePageName(noticeType)}
      />

      {noticeType === "total_notices" && (
        <div className="mt-4 mb-4">
          <div className="flex items-center flex-wrap gap-2">
            <div className="flex items-center">
              <div className="flex items-center">
                {selectedNoticeIds.length == 0 ? (
                  <Tooltip title="Please select a notice">
                    <div>
                      <Button
                        type="primary"
                        icon={<DeleteFilled />}
                        className="mr-2"
                        disabled
                      >
                        Bulk Delete
                      </Button>
                    </div>
                  </Tooltip>
                ) : (
                  <Button
                    type={"primary"}
                    icon={<DeleteFilled />}
                    onClick={() => setIsDeleteModalOpen(true)}
                    className="mr-2"
                    disabled={selectedNoticeIds.length > 0 ? false : true}
                  >
                    Bulk Delete
                  </Button>
                )}
                <BulkStatusButton
                  selectedIds={selectedNoticeIds}
                  doctype={() => (activeNoticeTab === "additional" ? "GST Additional Notice And Order" : "GST Notice And Order")}
                  onDone={refreshAfterDelete}
                />
                <DeleteAllNoticesButton
                  fetchAllIds={fetchAllNoticeIdsForDelete}
                  deleteByIds={deleteNoticesByIds}
                  onDone={refreshAfterDelete}
                  scopeHint="across all clients"
                />
              </div>
              <Button
                loading={isProcessingAll}
                icon={<Download className="w-4 h-4 mt-2" />}
                type="primary"
                onClick={() => setFetchConfirmOpen(true)}
                style={{
                  background: "#1890ff",
                  borderColor: "#1890ff",
                  border: "1px solid #fff",
                  boxShadow: "0 0 16px rgba(30, 64, 175, 0.4",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontWeight: "600",
                  color: "#fff",
                  transition: "box-shadow 0.3s ease",
                }}
                disabled={isProcessingAll}
              >
                {isProcessingAll
                  ? "Processing..."
                  : `Fetch Notices for All Clients`}
              </Button>
            </div>
            {/* Right-aligned group merged from the standalone row below.
                User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2) self-
                suppresses for single-user orgs and regular users — only
                admins see a functional dropdown. The standalone variant
                still renders for non-total_notices types. */}
            <div className="flex items-center gap-2">
              <DownloadDataButton
                entity="notices-gst"
                noun="notices"
                selectedIds={selectedNoticeIds}
                selectedRows={(notices || []).filter((r) =>
                  selectedNoticeIds.includes(r.id || r.name)
                )}
                fetchAllRecords={fetchAllForExport}
                fileBaseName="noticeai-gst-notices"
                title="GST Notices"
              />
              <UserFilterDropdown
                value={userFilter}
                onChange={setUserFilter}
                portal={serviceType}
              />
            </div>
          </div>
        </div>
      )}

      {noticeType == "failed_login" && (
        <div className="my-2 flex items-center flex-wrap gap-2">
          <Button
            loading={isProcessingAll}
            icon={<Download className="w-4 h-4 mt-2" />}
            type="primary"
            onClick={handleFetchAllFailedLoginClientsNotices}
            disabled={isProcessingAll || !failedLoginRecords}
            title={
              !failedLoginRecords
                ? "No failed-login clients to retry"
                : undefined
            }
          >
            {isProcessingAll
              ? "Processing..."
              : `Fetch notices for all Failed Login Clients`}
          </Button>
          {/* Inline UserFilter — Download Data is intentionally hidden on
              the Failed Logins view; the failed-login rows are only client
              + portal + initiated-time + don't have notice content to
              export. The Fetch button stays as the primary action. */}
          <UserFilterDropdown
            value={userFilter}
            onChange={setUserFilter}
            portal={serviceType}
          />
        </div>
      )}

      {/* Top-left Download + UserFilter strip for notice types that
          have NO primary action button (open / over_due / last_3_days /
          due_7_days). Mirrors the positioning used inside the
          total_notices and failed_login blocks above, so Download Data
          and All Users always render in the same top-left strip
          regardless of notice type. Self-suppresses for total_notices
          and failed_login since those types render the controls inline
          alongside their action button. */}
      {noticeType !== "total_notices" && noticeType !== "failed_login" && (
        <div className="flex items-center flex-wrap gap-2 mt-4 mb-2">
          {/* Delete Notice — same bulk-delete action the total_notices view
              offers, surfaced here so a client can delete their own notices
              on the per-client notice list (and the open/overdue/etc. views).
              Uses the existing handleDeleteNotices (both tabs) + confirm modal;
              the backend delete is org-scoped, so a user can only delete their
              own org's notices. Disabled until at least one row is selected. */}
          {selectedNoticeIds.length === 0 ? (
            <Tooltip title="Please select a notice">
              <div>
                <Button type="primary" icon={<DeleteFilled />} disabled>
                  Bulk Delete
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button
              type="primary"
              icon={<DeleteFilled />}
              onClick={() => setIsDeleteModalOpen(true)}
            >
              Bulk Delete
            </Button>
          )}
          <BulkStatusButton
            selectedIds={selectedNoticeIds}
            doctype={() => (activeNoticeTab === "additional" ? "GST Additional Notice And Order" : "GST Notice And Order")}
            onDone={refreshAfterDelete}
          />
          <DeleteAllNoticesButton
            fetchAllIds={fetchAllNoticeIdsForDelete}
            deleteByIds={deleteNoticesByIds}
            onDone={refreshAfterDelete}
            scopeHint="for this client"
            className=""
          />
          <DownloadDataButton
            entity="notices-gst"
            noun="notices"
            selectedIds={selectedNoticeIds}
            selectedRows={(notices || []).filter((r) =>
              selectedNoticeIds.includes(r.id || r.name)
            )}
            fetchAllRecords={fetchAllForExport}
            fileBaseName="noticeai-gst-notices"
            title="GST Notices"
          />
          <UserFilterDropdown
            value={userFilter}
            onChange={setUserFilter}
            portal={serviceType}
          />
        </div>
      )}

      {noticeType !== "failed_login" && (
        <div className="flex gap-2 mb-4 mt-4">
          {/* Additional Notices is positioned FIRST (and is the default tab)
              by product decision. Notices & Orders follows. */}
          <button
            onClick={() => {
              setActiveNoticeTab("additional");
              setLimitStart(0);
              setFilterValues({
                client_name: null,
                ref_id: null,
                notice_type: null,
                type: null,
                notice_status: null,
                issuedDate: null,
                dueDate: null,
              });
              setSelectedNoticeIds([]);
            }}
            className={`flex-1 px-6 py-4 font-semibold text-base transition-all duration-300 relative ${
              activeNoticeTab === "additional"
                ? "bg-gray-100 text-blue-600 border-b-4 border-blue-600"
                : "bg-white text-gray-600 hover:bg-gray-50 border-b-2 border-gray-200"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span>Additional Notices ({additionalNoticeCount || 0})</span>
            </div>
          </button>

          <button
            onClick={() => {
              setActiveNoticeTab("normal");
              setLimitStart(0);
              setFilterValues({
                client_name: null,
                ref_id: null,
                notice_type: null,
                type: null,
                notice_status: null,
                issuedDate: null,
                dueDate: null,
              });
              setSelectedNoticeIds([]);
            }}
            className={`flex-1 px-6 py-4 font-semibold text-base transition-all duration-300 relative ${
              activeNoticeTab === "normal"
                ? "bg-gray-100 text-blue-600 border-b-4 border-blue-600"
                : "bg-white text-gray-600 hover:bg-gray-50 border-b-2 border-gray-200"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span>Notices & Orders ({normalNoticeCount || 0})</span>
            </div>
          </button>
        </div>
      )}


      {/* Batch fetch progress banner */}
      {fetchProgress && !fetchProgress.isComplete && (
        <div
          style={{
            marginBottom: "16px",
            padding: "16px 20px",
            background: "linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)",
            borderRadius: "12px",
            border: "1px solid #91d5ff",
          }}
        >
          {(() => {
            // Live per-notice progress, when the engine has written it.
            const np = fetchProgress.notices;
            const phaseLabel = {
              logging_in: "Signing in to GST portal…",
              downloading: "Downloading notices…",
              done: "Finalising…",
            };
            const nTotal = np?.notices_total || 0;
            const nDone = np?.notices_done || 0;
            const noticePct =
              nTotal > 0 ? Math.round((nDone / nTotal) * 100) : 0;
            const clientPct =
              fetchProgress.total > 0
                ? Math.round(
                    ((fetchProgress.successful + fetchProgress.failed) /
                      fetchProgress.total) *
                      100,
                  )
                : 0;
            // Prefer the per-notice bar once the engine reports a notice
            // total; otherwise show the client-level bar (e.g. during login
            // or while waiting for the first progress write).
            const useNotice = nTotal > 0 && (np?.phase || "") !== "done";
            return (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <SyncOutlined
                      spin
                      style={{ color: "#1890ff", fontSize: "16px" }}
                    />
                    <Text strong style={{ fontSize: "14px" }}>
                      {np?.phase
                        ? phaseLabel[np.phase] || "Fetching GST Notices…"
                        : "Fetching GST Notices…"}
                    </Text>
                  </div>
                  <Text style={{ color: "#666", fontSize: "13px" }}>
                    {useNotice
                      ? `${nDone} / ${nTotal} notices`
                      : `${fetchProgress.successful + fetchProgress.failed} / ${fetchProgress.total} clients processed`}
                  </Text>
                </div>
                <Progress
                  percent={useNotice ? noticePct : clientPct}
                  strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }}
                  size="small"
                />
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  {useNotice && np?.files_done > 0 && (
                    <span style={{ color: "#1890ff" }}>
                      {np.files_done} file(s) downloaded
                    </span>
                  )}
                  <span style={{ color: "#52c41a" }}>
                    {fetchProgress.successful} successful
                  </span>
                  {fetchProgress.failed > 0 && (
                    <span style={{ color: "#ff4d4f" }}>
                      {fetchProgress.failed} failed
                    </span>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      <TableV2
        columns={columns.filter(
          // Hide the whole "View Summary" column when the plan lacks
          // VIEW_NOTICE_SUMMARY (per-row button is already gated inside
          // NoticeSummaryViewer; this drops the leftover header + cells).
          (col) => col.key !== "view_summary" || summaryFeatureEnabled
        )}
        data={notices}
        limitStart={limitStart}
        limit={limit}
        totalRecords={
          noticeType == "failed_login"
            ? failedLoginRecords
            : activeNoticeTab == "normal"
            ? normalNoticeCount
            : additionalNoticeCount
        }
        setLimitStart={setLimitStart}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        type={noticeType == "failed_login" ? "failed_login" : "notices"}
        serviceType={serviceType}
        rowRedirection={
          noticeType == "failed_login"
            ? `/${ServiceType.GSTIN}/failed-login-details`
            : `/${ServiceType.GSTIN}/notice-details`
        }
        isLoading={loading}
        selectedIds={selectedNoticeIds}
        setSelectedIds={setSelectedNoticeIds}
        filterValues={filterValues}
        handleFilterValueChange={handleFilterValueChange}
        statusOptions={noticeStatusValues}
        title={getNoticePageName(noticeType)}
        onFetchAllRecords={fetchAllForExport}
      />

      <Modal
        title=""
        open={isDeleteModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
        zIndex={9999}
        style={{ top: -50 }}
      >
        <div className="flex items-center justify-center flex-col">
          <img src={WarningImage} width={250} height={250} />
          <p>Are you sure you want to Delete ?</p>
        </div>
      </Modal>

      {/* Initial queued modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CheckCircle color="#52c41a" size={20} />
            <span>Successfully Queued</span>
          </div>
        }
        open={showSuccessModal}
        onOk={() => {
          setShowSuccessModal(false);
          if (parallelFetchLogName) {
            startBatchPolling(parallelFetchLogName);
          }
        }}
        onCancel={() => {
          setShowSuccessModal(false);
          if (parallelFetchLogName) {
            startBatchPolling(parallelFetchLogName);
          }
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setShowSuccessModal(false);
              if (parallelFetchLogName) {
                startBatchPolling(parallelFetchLogName);
              }
            }}
          >
            Got It
          </Button>,
        ]}
        width={500}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: "16px", fontSize: "16px" }}>
            <strong>
              Notice fetching has been successfully queued for failed login clients.
            </strong>
          </p>

          <p style={{ marginBottom: "16px", color: "#666" }}>
            Notices are being fetched in the background. You will see a live
            progress bar and a notification when the process completes.
          </p>

          {processedClients.length > 0 && (
            <div>
              <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Queued for {processedClients.length} clients:
              </p>
              <div
                style={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  background: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                {processedClients.map((client, index) => (
                  <div key={index} style={{ padding: "2px 0" }}>
                    • {client}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Sync completion modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <CheckCircleOutlined
              style={{ color: fetchProgress?.failed > 0 ? "#faad14" : "#52c41a", fontSize: "20px" }}
            />
            <span>
              {fetchProgress?.failed > 0 && fetchProgress?.successful === 0
                ? "Sync Process Failed"
                : "Sync Process Completed"}
            </span>
          </div>
        }
        open={syncCompletionModal}
        onCancel={() => {
          setSyncCompletionModal(false);
          setFetchProgress(null);
          setParallelFetchLogName(null);
          setIsProcessingAll(false);
          getTotalGSTNoticeList(limitStart, limit);
          getNoticesCount();
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setSyncCompletionModal(false);
              setFetchProgress(null);
              setParallelFetchLogName(null);
              setIsProcessingAll(false);
              getTotalGSTNoticeList(limitStart, limit);
              getNoticesCount();
            }}
          >
            OK
          </Button>,
        ]}
        width={500}
        centered
        closable={false}
      >
        <div style={{ padding: "16px 0" }}>
          {fetchProgress ? (
            <>
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Title level={4} style={{ color: fetchProgress.failed > 0 ? "#faad14" : "#52c41a", marginBottom: "8px" }}>
                  GST Notice Fetching Complete
                </Title>
                <Text type="secondary">
                  All {fetchProgress.total} clients have been processed.
                </Text>
              </div>

              <div style={{
                display: "flex",
                justifyContent: "center",
                gap: "32px",
                marginBottom: "16px",
                padding: "16px",
                background: "#fafafa",
                borderRadius: "8px",
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "#52c41a" }}>
                    {fetchProgress.successful}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Successful</Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: fetchProgress.failed > 0 ? "#ff4d4f" : "#d9d9d9" }}>
                    {fetchProgress.failed}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Failed</Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1890ff" }}>
                    {fetchProgress.total}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Total</Text>
                </div>
              </div>

              {/* SUPER-ADMIN ONLY: fetch method + detailed timing button.
                  Normal users never see these. */}
              {isAdmin && (fetchProgress.fetchMethod || fetchProgress.hasTiming) && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "12px",
                    padding: "10px 14px",
                    background: "#f0f5ff",
                    border: "1px solid #d6e4ff",
                    borderRadius: "8px",
                  }}
                >
                  <div>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Fetch method
                    </Text>
                    <div style={{ fontWeight: 600 }}>
                      {({
                        requests: "API (direct, fast)",
                        api: "API (direct, fast)",
                        selenium: "Selenium (browser)",
                        "selenium-fallback": "Selenium (fallback)",
                        lambda: "Cloud function (Lambda)",
                      }[fetchProgress.fetchMethod]) ||
                        fetchProgress.fetchMethod ||
                        "-"}
                    </div>
                  </div>
                  {fetchProgress.hasTiming && fetchProgress.logId && (
                    <Button
                      size="small"
                      onClick={() =>
                        navigate(`/gstin/fetch-timing/${fetchProgress.logId}`)
                      }
                    >
                      View Timing
                    </Button>
                  )}
                </div>
              )}

              {fetchProgress.failed > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  {fetchProgress.portalFailureCount > 0 && (
                    <Alert
                      message={`${fetchProgress.portalFailureCount} client(s) could not be fetched due to portal connectivity issues. You may retry later.`}
                      type="warning"
                      showIcon
                      style={{ marginBottom: "8px" }}
                    />
                  )}
                  {fetchProgress.credentialFailureCount > 0 && (
                    <Alert
                      message={`${fetchProgress.credentialFailureCount} client(s) failed due to login credentials / other issues.`}
                      type="error"
                      showIcon
                      style={{ marginBottom: "8px" }}
                    />
                  )}
                  <Text type="secondary" style={{ fontSize: "13px", display: "block", textAlign: "center", marginTop: "4px" }}>
                    Check the Failed Login tab for details.
                  </Text>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", marginBottom: "16px" }}>
              <Title level={4} style={{ color: "#52c41a", marginBottom: "8px" }}>
                Notice Syncing Completed!
              </Title>
            </div>
          )}
        </div>
      </Modal>

      <FetchNoticesConfirmModal
        open={fetchConfirmOpen}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={(ids) => {
          setFetchConfirmOpen(false);
          handleFetchAllClientsNotices(ids);
        }}
        portal="gst"
      />
    </>
  );
};
